/** One further attempt, inside the shared CouchDB fetch wrapper, for the ONE 401 that
 *  repairs itself.
 *
 *  For a few seconds after an account is created, CouchDB can hold two conflicting
 *  versions of that account's `_users` document, and while that is true it refuses to
 *  authenticate the account at all — `401 unauthorized`, reason "User document conflicts
 *  must be resolved before the document is used for authentication purposes." The server
 *  repairs the document and retries the sign-in a few times on its own
 *  (`establishUserSessionHealingConflictsAsync`), but the repair can still be unfinished
 *  when the client's own retry runs out, and then the 401 reaches whichever read asked
 *  for a document. During a new account's first sync every read is served from the REMOTE
 *  database, so any of them can meet it. The underlying race is cybsol/mh-physique#4761.
 *
 *  Before this module the recovery lived in exactly one caller — `getFoodByIdAsync`,
 *  which retries because `classifyFoodReadError` answers `Transient` for this condition.
 *  Every other read simply came back empty for that pass, and each new read path that
 *  turned out to matter during first sync had to be found and taught the same lesson
 *  (deferred finding `df-c70dd5850b6f`). Putting the decision here puts it on the one
 *  path every read already goes through.
 *
 *  Why this is affordable, which is what deferred it. Checking costs reading the response
 *  body, and recovering costs a wait — both on the hottest path in the app, one the sync
 *  longpolls flow through as well. Neither is actually paid, because the wrapper asks
 *  these functions only about a 401 that has ALREADY exhausted the ordinary
 *  refresh-and-retry. A successful fetch never reads a body, and a request that is not
 *  already twice-refused never waits.
 */

import { isUsersDocConflictReason } from './error-patterns';

/** The shortest the client waits before its one extra attempt. Chosen to sit just above
 *  the server's own repair loop, which waits a jittered 100-300 ms between its sign-in
 *  attempts (`CONFLICT_REPAIR_JITTER_MS` in `couch-user-session.ts`): waiting less than
 *  that would re-ask before the repair it is waiting for has had a chance to land. */
export const USERS_DOC_CONFLICT_RETRY_BASE_WAIT_MS = 400;

/** Added to the base wait, scaled by a random number. The wait is jittered for the same
 *  reason the server's is: two sign-ins repairing the same document must not wait the
 *  identical length of time, or they keep re-forking it in lockstep. */
export const USERS_DOC_CONFLICT_RETRY_JITTER_MS = 400;

/** How long to wait before the one extra attempt, given a random number in `[0, 1)` —
 *  `Math.random()` in production, a fixed value in a test. Taking the randomness as an
 *  argument is what makes the wait a value a test can assert on. */
export function usersDocConflictRetryWaitMs(random: number): number {
  // clamp rather than trust the caller: a value outside [0, 1) would produce a wait
  // shorter than the base (which defeats the point) or an unbounded one
  const bounded = Math.min(Math.max(random, 0), 1);
  return USERS_DOC_CONFLICT_RETRY_BASE_WAIT_MS + Math.round(bounded * USERS_DOC_CONFLICT_RETRY_JITTER_MS);
}

/** Does this response body carry CouchDB's self-healing `_users`-conflict refusal?
 *
 *  CouchDB answers with a small JSON object — `{"error":"unauthorized","reason":"User
 *  document conflicts must be resolved before …"}` — so the sentence is read out of
 *  `reason`. A body that is not JSON at all is matched against its raw text instead,
 *  because something in front of CouchDB (a proxy, a gateway) may have reshaped the
 *  answer while keeping the wording; nothing else on the wire says "user document
 *  conflicts must be resolved", so a raw match cannot pick up an unrelated failure. */
export function isUsersDocConflictResponseBody(bodyText: string): boolean {
  if (!bodyText) return false;
  try {
    const parsed: unknown = JSON.parse(bodyText);
    if (typeof parsed === 'object' && parsed !== null) {
      const reason = (parsed as { reason?: unknown }).reason;
      return typeof reason === 'string' && isUsersDocConflictReason(reason);
    }
  } catch {
    // expected: a SyntaxError from JSON.parse whenever the body is not JSON, which is a
    // body shape this function is written to accept rather than a failure to report
  }
  return isUsersDocConflictReason(bodyText);
}

/** The parts of a `Response` this module reads. Declared structurally so a test can hand
 *  in a plain object rather than build a real `Response`. */
export interface ClonableResponseLike {
  clone(): { text(): Promise<string> };
}

/** Read a 401 response and say whether it is the self-healing `_users` conflict.
 *
 *  Reads a CLONE, so the response handed back to PouchDB still has its body unread — a
 *  caller that consumed the original would leave PouchDB with a response it cannot parse.
 *
 *  Any failure answers `false`. Failing this way round is the safe direction: the worst
 *  it costs is the extra attempt not being taken, which is exactly what happened before
 *  this existed, whereas letting the failure escape would turn a response the caller
 *  could have handled into a thrown error. */
export async function isUsersDocConflictResponseAsync(response: ClonableResponseLike): Promise<boolean> {
  try {
    const bodyText = await response.clone().text();
    return isUsersDocConflictResponseBody(bodyText);
  } catch {
    // handed on: the failure that matters here is the refusal itself, and answering "no"
    // returns that refusal to the caller untouched — exactly what happened before this
    // recovery existed. Nothing is absorbed: the caller still sees the response and takes
    // its own failure path.
    return false;
  }
}

/** Wait `waitMs`, or stop early if `signal` is aborted while waiting.
 *
 *  It RESOLVES on abort rather than rejecting: the caller is the fetch wrapper's loop, and
 *  the next iteration already refuses an aborted request with the reason the aborter gave.
 *  Throwing a second, different error from here would replace that with a worse one.
 *
 *  Without this the wrapper honoured a cancelled sync up to a whole wait late. Every other
 *  wait inside that function is bounded for the same reason (#4504). */
export function awaitUnlessAbortedAsync(waitMs: number, signal?: AbortSignal | null): Promise<void> {
  if (signal?.aborted) return Promise.resolve();
  return new Promise<void>(resolve => {
    const finish = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, waitMs);
    signal?.addEventListener('abort', finish, { once: true });
  });
}
